import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/* ═══════════════════════════════════════════════════════════════════
   SIGNATURE REPLAY — fullscreen royal animation
   • Emerges from CENTER with a golden light burst
   • Replays strokes one-by-one with gold→blue gradient ink + glow
   • Falls back to a static signature_data_url if strokes missing
   • Tap anywhere to vanish; auto-fades 1.6s after replay completes
   ═══════════════════════════════════════════════════════════════════ */

const GOLD = '#D4AF37';
const GOLD_LIGHT = '#F5D97A';
const QUANTUM = '#1E6FE3';

export const SignatureReplay = ({
  strokes,
  fallbackImage,
  onClose,
  speed = 1.0,
}) => {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const animFrame = useRef(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    if (!strokes || !strokes.strokes || !strokes.strokes.length) return;
    const cv = canvasRef.current;
    const wrap = wrapRef.current;
    if (!cv || !wrap) return;

    // Compute tight bounding box of the signature so the replay fills the centre
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    strokes.strokes.forEach((stroke) => {
      stroke.forEach((p) => {
        if (p.x < minX) minX = p.x;
        if (p.y < minY) minY = p.y;
        if (p.x > maxX) maxX = p.x;
        if (p.y > maxY) maxY = p.y;
      });
    });
    if (!isFinite(minX)) return;
    const sw = Math.max(maxX - minX, 1);
    const sh = Math.max(maxY - minY, 1);

    // Fit centred to 70vw × 50vh
    const targetW = wrap.clientWidth * 0.74;
    const targetH = wrap.clientHeight * 0.5;
    const scale = Math.min(targetW / sw, targetH / sh);
    const renderW = sw * scale;
    const renderH = sh * scale;
    const offsetX = (wrap.clientWidth - renderW) / 2 - minX * scale;
    const offsetY = (wrap.clientHeight - renderH) / 2 - minY * scale;

    const ratio = window.devicePixelRatio || 1;
    cv.width = wrap.clientWidth * ratio;
    cv.height = wrap.clientHeight * ratio;
    cv.style.width = `${wrap.clientWidth}px`;
    cv.style.height = `${wrap.clientHeight}px`;
    const ctx = cv.getContext('2d');
    ctx.scale(ratio, ratio);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = Math.max(4, 4.6 * scale);

    // Build gold→blue gradient anchored to the signature's bounding box centre
    const cx = wrap.clientWidth / 2;
    const cy = wrap.clientHeight / 2;
    // Iconic beam palette: emerald → sapphire → violet → crimson → gold.
    // Applied as a single stroke gradient so the signature reads as a
    // classy multi-beam sweep — no shapes, no bursts, just the ink alive.
    const BEAM_EMERALD = '#10B981';
    const BEAM_SAPPHIRE = '#0F52BA';
    const BEAM_VIOLET = '#8B5CF6';
    const BEAM_CRIMSON = '#DC143C';
    const BEAM_GOLD = '#D4AF37';
    const buildGrad = () => {
      const g = ctx.createLinearGradient(cx - renderW / 2, cy, cx + renderW / 2, cy);
      g.addColorStop(0.00, BEAM_EMERALD);
      g.addColorStop(0.25, BEAM_SAPPHIRE);
      g.addColorStop(0.50, BEAM_VIOLET);
      g.addColorStop(0.75, BEAM_CRIMSON);
      g.addColorStop(1.00, BEAM_GOLD);
      return g;
    };

    // Total duration based on the recorded timeline
    const totalDuration = (() => {
      let last = 0;
      strokes.strokes.forEach((st) => {
        if (st.length) last = Math.max(last, st[st.length - 1].t || 0);
      });
      return Math.max(last, 700);
    })();

    // Delay replay so the intro fades in first — no shape burst.
    const startedAt = performance.now() + 400;

    const tx = (p) => offsetX + p.x * scale;
    const ty = (p) => offsetY + p.y * scale;

    const tick = () => {
      const now = performance.now();
      if (now < startedAt) {
        animFrame.current = requestAnimationFrame(tick);
        return;
      }
      const elapsed = (now - startedAt) * speed;
      ctx.clearRect(0, 0, cv.width, cv.height);
      ctx.strokeStyle = buildGrad();
      // Soft chromatic bloom — no coloured discs anywhere.
      ctx.shadowColor = 'rgba(139,92,246,0.85)';
      ctx.shadowBlur = 28;

      let stillDrawing = false;
      strokes.strokes.forEach((stroke) => {
        if (stroke.length < 2) return;
        const strokeStart = stroke[0].t || 0;
        if (elapsed < strokeStart) return;
        ctx.beginPath();
        ctx.moveTo(tx(stroke[0]), ty(stroke[0]));
        for (let i = 1; i < stroke.length; i++) {
          const p = stroke[i];
          if ((p.t || 0) > elapsed) {
            stillDrawing = true;
            const prev = stroke[i - 1];
            const pt = (p.t || 0);
            const pp = (prev.t || 0);
            const r = pt === pp ? 1 : (elapsed - pp) / (pt - pp);
            const ix = prev.x + (p.x - prev.x) * r;
            const iy = prev.y + (p.y - prev.y) * r;
            ctx.lineTo(tx({ x: ix, y: iy }), ty({ x: ix, y: iy }));
            break;
          } else {
            ctx.lineTo(tx(p), ty(p));
          }
        }
        ctx.stroke();
      });

      if (elapsed < totalDuration || stillDrawing) {
        animFrame.current = requestAnimationFrame(tick);
      } else {
        setDone(true);
        setTimeout(() => onClose && onClose(), 1600);
      }
    };

    animFrame.current = requestAnimationFrame(tick);
    return () => {
      if (animFrame.current) cancelAnimationFrame(animFrame.current);
    };
  }, [strokes, speed, onClose]);

  // Auto-dismiss for fallback static-image path
  useEffect(() => {
    if (strokes && strokes.strokes && strokes.strokes.length) return;
    if (!fallbackImage) return;
    const t = setTimeout(() => onClose && onClose(), 3200);
    return () => clearTimeout(t);
  }, [strokes, fallbackImage, onClose]);

  const hasStrokes = !!(strokes && strokes.strokes && strokes.strokes.length);

  return (
    <AnimatePresence>
      <motion.div
        ref={wrapRef}
        key="sig-replay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4 }}
        onClick={onClose}
        className="fixed inset-0 z-[200] flex items-center justify-center cursor-pointer overflow-hidden"
        style={{
          background:
            /* Layered cinematic beams — a soft green-blue-violet-red aurora
               centered behind the signature, so the ink pops against colour
               instead of a flat void. Kept low-opacity so strokes stay sharp. */
            'radial-gradient(circle at 30% 40%, rgba(16,185,129,0.24) 0%, rgba(0,0,0,0) 42%), ' +
            'radial-gradient(circle at 70% 40%, rgba(15,82,186,0.28) 0%, rgba(0,0,0,0) 42%), ' +
            'radial-gradient(circle at 50% 65%, rgba(139,92,246,0.30) 0%, rgba(0,0,0,0) 46%), ' +
            'radial-gradient(circle at 30% 75%, rgba(220,20,60,0.22) 0%, rgba(0,0,0,0) 42%), ' +
            'radial-gradient(circle at 50% 50%, rgba(212,175,55,0.10) 0%, rgba(0,0,0,0) 55%), ' +
            'linear-gradient(180deg, #05030B 0%, #020106 50%, #06030C 100%)',
          backdropFilter: 'blur(10px)',
        }}
        data-testid="signature-replay"
      >
        {/* Sharp signature-gradient frame at the window edge + gradient "Bliss App" title inside. */}
        <div className="absolute pointer-events-none" data-testid="signature-replay-frame"
          style={{
            top: 'var(--safe-area-inset-top, env(safe-area-inset-top, 0px))',
            bottom: 'var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px))',
            left: 0, right: 0,
            border: '3px solid transparent',
            borderImage: 'linear-gradient(135deg, #10B981, #1E6FE3, #8B5CF6, #DC143C, #10B981) 1',
            boxShadow: 'inset 0 0 18px rgba(139,92,246,0.45)',
          }}/>
        <div className="absolute left-0 right-0 flex justify-center pointer-events-none"
          style={{ top: 'calc(var(--safe-area-inset-top, env(safe-area-inset-top, 0px)) + 18px)' }}>
          <span className="font-['Cinzel'] uppercase" data-testid="signature-replay-title"
            style={{ fontSize: 14, letterSpacing: '0.42em', paddingLeft: '0.42em', lineHeight: '22px', fontWeight: 600,
              background: 'linear-gradient(90deg, #10B981, #0F52BA, #8B5CF6, #DC143C)', WebkitBackgroundClip: 'text', backgroundClip: 'text',
              color: 'transparent', filter: 'drop-shadow(0 0 6px rgba(139,92,246,0.6))' }}>
            Bliss App
          </span>
        </div>

        {/* Animated stroke canvas — the ink IS the show. No discs, no rings,
            no sparkles. Just the pure multi-beam signature glowing in the
            iconic emerald → sapphire → violet → crimson → gold sweep. */}
        {hasStrokes && (
          <canvas
            ref={canvasRef}
            className="absolute inset-0 pointer-events-none"
          />
        )}

        {/* Fallback static signature — emerges from the burst */}
        {!hasStrokes && fallbackImage && (
          <>
            <motion.img
              src={fallbackImage}
              alt="Your signature"
              initial={{ opacity: 0, scale: 0.6, filter: 'blur(8px)' }}
              animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
              transition={{ duration: 0.9, delay: 0.2, ease: 'easeOut' }}
              className="relative max-w-[78vw] max-h-[55vh] pointer-events-none"
              style={{
                filter:
                  'drop-shadow(0 0 22px rgba(16,185,129,0.55)) drop-shadow(0 0 44px rgba(15,82,186,0.55)) drop-shadow(0 0 66px rgba(139,92,246,0.55)) drop-shadow(0 0 88px rgba(220,20,60,0.45))',
              }}
            />
            {/* Re-sign hint so the user knows they need to capture a fresh
                signature to unlock the live drawing animation. */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.3, duration: 0.5 }}
              className="absolute bottom-24 px-4 py-2 rounded-full pointer-events-none"
              style={{
                background: 'rgba(0,0,0,0.55)',
                border: `1px solid ${GOLD}55`,
              }}
            >
              <span className="font-['Outfit'] text-[10px] uppercase tracking-[0.2em]" style={{ color: GOLD }}>
                Re-sign in Try → Signature → Sign to enable live animation
              </span>
            </motion.div>
          </>
        )}

        {/* Empty-state */}
        {!hasStrokes && !fallbackImage && (
          <span className="font-['Cormorant_Garamond'] italic text-2xl text-white/40 relative z-10">
            No signature sealed yet
          </span>
        )}

        {/* Dismiss hint */}
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: done ? 0.6 : 0.25 }}
          transition={{ delay: 0.4 }}
          className="absolute bottom-12 font-['Outfit'] text-[10px] uppercase tracking-[0.4em]"
          style={{ color: GOLD, pointerEvents: 'none' }}
        >
          Tap anywhere to dismiss
        </motion.span>
      </motion.div>
    </AnimatePresence>
  );
};

export default SignatureReplay;
